/* eslint-disable react/jsx-key */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import cn from 'classnames';
import { isNil } from 'lodash';
import numeral from 'numeral';
import { theme } from 'antd';

import { StationTier } from '@/constants/enums';
import { useStores } from '@/hooks/use-stores';
import Chip from '../Chip';
import EPRequestTierModal from './EPRequestTierModal';
import EPTierLock from './EPTierLock';
import CheckIcon from './icons/CheckIcon';
import { EPSystemControlPaymentPlanCardContainer, EPTierPaymentPlanCardContainer, StyledIconPlay } from './styled';

interface EPTierPaymentPlanCardProps {
  title: string;
  description: string;
  assetCount: number;
  expectedAnnualSavings: { min: number; max: number };
  expectedROI: number;
  featuresTitle: string;
  features: React.ReactElement[];
  onRequestTier: () => void;
  pricePerAsset?: number;
  monthlyPriceBilledAnnually?: number;
  monthlyPrice?: number;
  tierRequestChipText?: string;
  className?: string;
  fromOfferingPage?: boolean;
  type?: StationTier;
}

const { useToken } = theme;

const EPTierPaymentPlanCard = observer(
  ({
    title,
    description,
    pricePerAsset,
    assetCount,
    monthlyPriceBilledAnnually,
    monthlyPrice,
    expectedAnnualSavings,
    expectedROI,
    featuresTitle,
    features,
    tierRequestChipText,
    onRequestTier,
    className,
    fromOfferingPage,
    type,
  }: EPTierPaymentPlanCardProps) => {
    const { t } = useTranslation();
    const { stationStore } = useStores();
    const [isTierRequestModalOpen, setIsTierRequestModalOpen] = useState(false);
    const { token } = useToken();

    const onRequestTierHandler = () => {
      onRequestTier && onRequestTier();
      setIsTierRequestModalOpen(true);
    };

    const onModalClose = () => {
      setIsTierRequestModalOpen(false);
    };

    const onTierLockClick = () => window.open('https://www.youtube.com/watch?v=K9VnEvnO3Vk', '_blank');

    return (
      <>
        <EPTierPaymentPlanCardContainer className={className}>
          <div className="header">
            <div className="card-title-container">
              <span className="card-title">{t(title)}</span>
              {fromOfferingPage && stationStore.stationTier === type && (
                <span className="your-current-version">{t('yourCurrentVersion')}</span>
              )}
            </div>
            <div className="horizontal-line" />
            <span className="card-description">{t(description)}</span>
            <EPTierLock
              label={t('learnMoreAndWatchVideo')}
              icon={<StyledIconPlay height={18} />}
              shouldShowTooltip={false}
              onClickHandler={onTierLockClick}
            />
          </div>
          <div className="body">
            <div className={cn('price-per-asset', isNil(pricePerAsset) && 'hidden')}>
              <span className="price-per-asset-value">+{numeral(pricePerAsset).format(0, 0)}$</span>
              &nbsp;/
              {t('mon')}&nbsp;
              {t('perAsset')}
            </div>
            <span className="asset-count">{t('assetCount', { assetCount })}</span>
            {isNil(monthlyPriceBilledAnnually) ? (
              <div className="get-your-price">{t('getYourPrice')}</div>
            ) : (
              <div className="monthly-price-container">
                <span className="monthly-price-value">+{monthlyPriceBilledAnnually}$</span>
                <span className="monthly-price-period">/{t('mon')}</span>
              </div>
            )}
            <span className={cn('billed-annually', isNil(pricePerAsset) && 'hidden')}>
              {t('billedAnnually')}
              <span className="monthly-price-highlighted">${monthlyPrice}</span>
              {t('monthly')}
            </span>
            <div className="horizontal-line" />
            <div className="expected-annual-savings-container">
              <span className="expected-annual-savings-label">{t('expectedAnnualSavings')}</span>
              <span className="expected-annual-savings-value">
                {numeral(expectedAnnualSavings?.min).format('0,0')} -{' '}
                {numeral(expectedAnnualSavings?.max).format('0,0')} USD
              </span>
            </div>
            <div className="expected-roi-container">
              <span className="expected-roi-label">{t('expectedROI')}</span>
              <span className="expected-roi-value">
                <Chip title={t('years', { years: numeral(expectedROI).format('0.0') })} className="expected-roi-chip" />
              </span>
            </div>
          </div>
          <div className="footer">
            <Chip
              title={t(tierRequestChipText ?? 'startOneMonthTrial')}
              className="start-one-month-trial-chip"
              onClick={onRequestTierHandler}
            />
            <span className="or-purchase-now-label" onClick={onRequestTierHandler}>
              {t('orPurchaseNow')}
            </span>
            <span className="feature-title">{t(featuresTitle)}</span>
            {features?.map((feature, i) => (
              <div className="feature" key={`feature-${i}`}>
                <div className="green-check">
                  <CheckIcon color={token.colorBgContainer} />
                </div>
                {feature}
              </div>
            ))}
          </div>
          <EPRequestTierModal isOpen={isTierRequestModalOpen} onClose={onModalClose} />
        </EPTierPaymentPlanCardContainer>
      </>
    );
  },
);

export default EPTierPaymentPlanCard;

export const EPSystemTierPaymentPlanCard = observer(
  ({
    onRequestTier,
    className,
    fromOfferingPage,
  }: {
    onRequestTier;
    className?: string;
    fromOfferingPage?: boolean;
  }) => {
    const { t } = useTranslation();
    const { stationStore } = useStores();

    return (
      <EPTierPaymentPlanCard
        fromOfferingPage={fromOfferingPage}
        className={className}
        title={'systemView'}
        type={StationTier.Monitor}
        description={'systemViewDescription'}
        pricePerAsset={stationStore.tierPaymentPlan?.[StationTier.Monitor]?.pricePerAsset}
        assetCount={stationStore.tierPaymentPlan?.[StationTier.Monitor]?.assetCount}
        monthlyPriceBilledAnnually={stationStore.tierPaymentPlan?.[StationTier.Monitor]?.monthlyPriceBilledAnnually}
        monthlyPrice={stationStore.tierPaymentPlan?.[StationTier.Monitor]?.monthlyPriceBilledMonthly}
        expectedAnnualSavings={{
          min: stationStore.tierPaymentPlan?.[StationTier.Monitor]?.expectedAnnualSavingsMin,
          max: stationStore.tierPaymentPlan?.[StationTier.Monitor]?.expectedAnnualSavingsMax,
        }}
        expectedROI={stationStore.tierPaymentPlan?.[StationTier.Monitor]?.expectedROI}
        featuresTitle={'allAssetViewFeatures'}
        onRequestTier={onRequestTier}
        features={[
          <span>
            {t('individual')}&nbsp;
            <span className="highlighted-white-text">{t('pidMap')}</span>&nbsp;
            {t('monitorAndSupervise')}
          </span>,
          <span>
            <span className="highlighted-white-text">{t('systemHealthStatus')}</span>&nbsp;
            {t('isPartOf')}
          </span>,
          <span>
            {t('individual')}&nbsp;
            <span className="highlighted-white-text">{t('systemInsights')}</span>&nbsp;
            {t('improveSystemPerformance')}
          </span>,
          <span>
            <span className="highlighted-white-text">{t('derivedSystemEfficiencyParameters')}</span>&nbsp;
            {t('suchAs')}&nbsp;
            <span className="highlighted-white-text">{t('systemTrends')}</span>&nbsp;
            {t('toAnalyzeAndOptimize')}
          </span>,
        ]}
      />
    );
  },
);

export const EPSimpleControlPaymentPlanCard = observer(({ onRequestTier, fromOfferingPage }) => {
  const { t } = useTranslation();
  const { stationStore } = useStores();

  return (
    <EPTierPaymentPlanCard
      fromOfferingPage={fromOfferingPage}
      type={StationTier.SimpleControl}
      title={'simpleControl'}
      description={'simpleControlDescription'}
      pricePerAsset={stationStore.tierPaymentPlan?.[StationTier.SimpleControl]?.pricePerAsset}
      assetCount={stationStore.tierPaymentPlan?.[StationTier.SimpleControl]?.assetCount}
      monthlyPriceBilledAnnually={stationStore.tierPaymentPlan?.[StationTier.SimpleControl]?.monthlyPriceBilledAnnually}
      monthlyPrice={stationStore.tierPaymentPlan?.[StationTier.SimpleControl]?.monthlyPriceBilledMonthly}
      expectedAnnualSavings={{
        min: stationStore.tierPaymentPlan?.[StationTier.SimpleControl]?.expectedAnnualSavingsMin,
        max: stationStore.tierPaymentPlan?.[StationTier.SimpleControl]?.expectedAnnualSavingsMax,
      }}
      expectedROI={stationStore.tierPaymentPlan?.[StationTier.SimpleControl]?.expectedROI}
      featuresTitle={'allSystemViewFeatures'}
      tierRequestChipText={'learnMoreAndRequestTrial'}
      onRequestTier={onRequestTier}
      features={[
        <span>
          <span className="highlighted-white-text">{t('simpleControlEngine')}</span>&nbsp;
          {t('simpleControlEngine')}
        </span>,
        <span>
          <span className="highlighted-white-text">{t('energySavingsWidget')}</span>&nbsp;
          {t('toProvideComprehensiveOverview')}
        </span>,
        <span>
          <span className="highlighted-white-text">{t('greenhouseGasEmission')}</span>&nbsp;
          {t('savingsBasedOn')}
        </span>,
      ]}
    />
  );
});

export const EPDynamicControlPaymentPlanCard = observer(({ onRequestTier, fromOfferingPage }) => {
  const { t } = useTranslation();
  const { stationStore } = useStores();

  return (
    <EPTierPaymentPlanCard
      fromOfferingPage={fromOfferingPage}
      type={StationTier.Optimize}
      title={'optimize'}
      description={'dynamicControlDescription'}
      assetCount={stationStore.tierPaymentPlan?.[StationTier.Optimize]?.assetCount}
      expectedAnnualSavings={{
        min: stationStore.tierPaymentPlan?.[StationTier.Optimize]?.expectedAnnualSavingsMin,
        max: stationStore.tierPaymentPlan?.[StationTier.Optimize]?.expectedAnnualSavingsMax,
      }}
      expectedROI={stationStore.tierPaymentPlan?.[StationTier.Optimize]?.expectedROI}
      featuresTitle={'allSimpleControlFeatures'}
      tierRequestChipText={'learnMoreAndRequestTrial'}
      onRequestTier={onRequestTier}
      features={[
        <span>
          <span className="highlighted-white-text">{t('dynamicControlEngine')}</span>&nbsp;
          {t('predictiveAlgorithms')}
        </span>,
        <span>
          <span className="highlighted-white-text">{t('leakageWidget')}</span>&nbsp;
          {t('forDetectingAndIsolating')}
        </span>,
        <span>
          <span className="highlighted-white-text">{t('continuousAutiding')}</span>&nbsp;
          {t('reviewYourSystemPerformance')}
        </span>,
      ]}
    />
  );
});

export const EPSystemControlPaymentPlanCard = ({
  onRequestTier,
  className,
  fromOfferingPage,
}: {
  onRequestTier;
  className?: string;
  fromOfferingPage?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <EPSystemControlPaymentPlanCardContainer className={className} $fromOfferingPage={fromOfferingPage}>
      <div className="dashed-border-container">
        <div className="system-control-title-chip">{t('systemControl')}</div>
        <EPSimpleControlPaymentPlanCard onRequestTier={onRequestTier} fromOfferingPage={fromOfferingPage} />
        <EPDynamicControlPaymentPlanCard onRequestTier={onRequestTier} fromOfferingPage={fromOfferingPage} />
      </div>
    </EPSystemControlPaymentPlanCardContainer>
  );
};
